import { ReactNode } from 'react';
import './EstabelecimentoForm.css';
import { ChevronRight } from 'lucide-react';
import type { AppIcon } from '../../../../components/BrandIcons';

export const SectionIcon = ({ icon: Icon, className = '' }: { icon: AppIcon; className?: string }) => (
    <span className={`flex w-5 shrink-0 items-center justify-center text-[0.9rem] ${className}`} aria-hidden>
        <Icon />
    </span>
);

export type SettingsNavItem<K extends string> = {
    key: K;
    title: string;
    description: string;
    icon: AppIcon;
    trailing?: ReactNode;
};

type SettingsNavProps<K extends string> = {
    label: string;
    items: SettingsNavItem<K>[];
    activeKey: K;
    onOpen: (key: K) => void;
    onSelect: (key: K) => void;
};

export function SettingsNav<K extends string>({ label, items, activeKey, onOpen, onSelect }: SettingsNavProps<K>) {
    return (
        <nav aria-label={label}>
            {/* Mobile: lista agrupada com chevron (iOS). */}
            <ul className="estab-group lg:hidden">
                {items.map((item) => (
                    <li key={item.key} className="estab-row estab-row--nav">
                        <button
                            type="button"
                            className="flex min-h-[3.25rem] w-full items-center gap-3 px-4 py-2 text-left active:bg-base-content/5"
                            onClick={() => onOpen(item.key)}
                        >
                            <SectionIcon icon={item.icon} className="text-base-content/50" />
                            <span className="min-w-0 flex-1">
                                <span className="block text-[0.9375rem] text-base-content">{item.title}</span>
                                <span className="block truncate text-xs text-base-content/55">
                                    {item.description}
                                </span>
                            </span>
                            {item.trailing}
                            <ChevronRight className="text-xs text-base-content/30" aria-hidden="true" />
                        </button>
                    </li>
                ))}
            </ul>

            {/* Desktop: barra lateral com selecao preenchida (macOS). */}
            <ul className="hidden space-y-0.5 lg:block">
                {items.map((item) => {
                    const isActive = item.key === activeKey;
                    return (
                        <li key={item.key}>
                            <button
                                type="button"
                                aria-current={isActive ? 'page' : undefined}
                                className={`flex w-full items-center gap-3 rounded-[0.5rem] px-2.5 py-2 text-left text-sm transition-colors ${
                                    isActive
                                        ? 'bg-base-content/[0.07] font-semibold text-base-content'
                                        : 'text-base-content/70 hover:bg-base-content/[0.04] hover:text-base-content'
                                }`}
                                onClick={() => onSelect(item.key)}
                            >
                                <SectionIcon
                                    icon={item.icon}
                                    className={isActive ? 'text-base-content/80' : 'text-base-content/45'}
                                />
                                <span className="min-w-0 flex-1 truncate">{item.title}</span>
                                {item.trailing}
                            </button>
                        </li>
                    );
                })}
            </ul>
        </nav>
    );
}

type SettingsSectionProps = {
    id?: string;
    title?: string;
    footer?: ReactNode;
    children: ReactNode;
    padded?: boolean;
};

export const SettingsSection = ({ id, title, footer, children, padded = false }: SettingsSectionProps) => (
    <section id={id} className="estab-section">
        {title && <h2 className="estab-section-header">{title}</h2>}
        <div className={`estab-group ${padded ? 'estab-group--padded' : ''}`}>{children}</div>
        {footer && <div className="estab-section-footer">{footer}</div>}
    </section>
);

type SettingsRowProps = {
    label?: ReactNode;
    htmlFor?: string;
    description?: ReactNode;
    children?: ReactNode;
    stacked?: boolean;
    inline?: boolean;
};

export const SettingsRow = ({ label, htmlFor, description, children, stacked = false, inline = false }: SettingsRowProps) => {
    const rowClass = ['estab-row', stacked && 'estab-row--stacked', inline && 'estab-row--inline']
        .filter(Boolean)
        .join(' ');

    return (
        <div className={rowClass}>
            {label && (
                <div className="min-w-0">
                    <label className="estab-row-label" htmlFor={htmlFor}>
                        {label}
                    </label>
                    {description && <div className="estab-row-description">{description}</div>}
                </div>
            )}
            {/* Sem rotulo, os filhos ocupam as colunas da linha diretamente (ex.: acao a esquerda e valor a direita). */}
            {children && (label ? <div className="estab-row-control">{children}</div> : children)}
        </div>
    );
};
